/**
 * WebSocket hook for real-time dashboard events — Phase 5.
 *
 * Connects to ws://host/ws/dashboard?token=<jwt>
 * Company context is derived from the JWT (server-side) — not client-supplied.
 * Auto-reconnects with exponential backoff on disconnect.
 * Close codes from the backend's access re-check (H20):
 *   1008 (policy violation): the user lost the token's company or is inactive. No
 *        reconnect: the same recovery as an HTTP 403 COMPANY_ACCESS_REVOKED runs (one
 *        refresh, then a reload into the company the refresh chose; sign-out if none).
 *        Within the recovery cooldown: one reconnect when it ends. Within 2 min of a
 *        successful recovery in this tab: stay closed and show "Live updates paused".
 *   1013 (try again later): the check itself failed (DB/Redis down). Normal backoff,
 *        no refresh.
 * Returns the latest event and connection status.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  companyRecoveryCooldownRemaining,
  recoverCompanyAccess,
  recoveredRecently,
  showLiveUpdatesPaused,
} from '@/api/sessionRecovery';

/** WebSocket close code the backend uses when company access is gone. */
export const WS_POLICY_VIOLATION = 1008;
/** WebSocket close code the backend uses when the access check itself failed. */
export const WS_TRY_AGAIN_LATER = 1013;

interface WebSocketEvent {
  type: string;
  company_id?: string;
  timestamp?: string;
  [key: string]: unknown;
}

interface UseWebSocketOptions {
  companyId?: string;
  enabled?: boolean;
  maxReconnectAttempts?: number;
}

interface UseWebSocketReturn {
  lastEvent: WebSocketEvent | null;
  isConnected: boolean;
  sendMessage: (msg: string) => void;
}

export function useWebSocket(options: UseWebSocketOptions = {}): UseWebSocketReturn {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { companyId: _companyId, enabled = true, maxReconnectAttempts = 5 } = options;
  const [lastEvent, setLastEvent] = useState<WebSocketEvent | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const unmounted = useRef(false);

  const connect = useCallback(() => {
    if (!enabled) return;

    // httpOnly cookies are sent automatically by the browser on same-origin
    // WebSocket connections — no manual token extraction needed.
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    // company_id is extracted from the JWT server-side — not sent as a param
    const url = `${protocol}//${host}/ws/dashboard`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as WebSocketEvent;
          if (data.type !== 'pong') {
            setLastEvent(data);
          }
        } catch {
          // Ignore non-JSON messages
        }
      };

      ws.onclose = (event: CloseEvent) => {
        setIsConnected(false);
        wsRef.current = null;
        if (unmounted.current) return;

        if (event?.code === WS_POLICY_VIOLATION && recoveredRecently()) {
          // This tab recovered under two minutes ago and access is gone again: the recovery
          // didn't help. Stay closed (no refresh-and-reload loop) and say so.
          showLiveUpdatesPaused();
          return;
        }
        if (event?.code === WS_POLICY_VIOLATION) {
          // Shared with the HTTP path, including its cooldown (sessionStorage). Within the
          // cooldown, schedule ONE reconnect for when it ends (so the bell isn't dead for
          // good); a 1008 then runs the recovery, which is allowed again by then.
          if (!recoverCompanyAccess()) {
            reconnectTimer.current = setTimeout(connect, companyRecoveryCooldownRemaining() + 50);
          }
          return;
        }
        // 1013 and every other close: the normal backoff below.

        // Auto-reconnect with exponential backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current += 1;
          reconnectTimer.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // Connection failed
    }
  }, [enabled, maxReconnectAttempts]);

  const sendMessage = useCallback((msg: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(msg);
    }
  }, []);

  // Heartbeat ping every 30 seconds
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      sendMessage('ping');
    }, 30000);

    return () => clearInterval(interval);
  }, [isConnected, sendMessage]);

  // Connect/disconnect lifecycle
  useEffect(() => {
    unmounted.current = false;
    connect();

    return () => {
      unmounted.current = true;
      if (reconnectTimer.current) {
        clearTimeout(reconnectTimer.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [connect]);

  return { lastEvent, isConnected, sendMessage };
}
